import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import {
  Spin,
  message,
  Button,
  Input,
  Form,
  DatePicker,
  Select,
  Radio,
  Modal,
  Space,
  Typography,
  Divider,
  Switch,
  Popconfirm,
  Alert,
  Tooltip,
} from 'antd';
import {
  SaveOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  CopyOutlined,
  PlusOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import Dropzone from 'react-dropzone';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { EmptyState, StatusBadge } from '../../common';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// 記事編集ページ（role 9 専用）
// URL: /article/:seriesCode/:articleId/edit
// - 編集可能: title / summary / content_md / target / tags / reading_time /
//             publish_date / instructor_email / thumbnail / status / attached_images（一覧+削除のみ）
// - 編集不可: 所属（series/edition/category, position）→ シリーズホームの編集モードから
//             attached_images の新規追加 → 後続フェーズで対応予定
function ArticleEditPage(props) {
  const params = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [article, setArticle] = useState(null);
  const [series, setSeries] = useState(null);

  // フォーム状態
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState(''); // サブタイトル（新規）
  const [summary, setSummary] = useState('');
  const [contentMd, setContentMd] = useState('');
  const [target, setTarget] = useState('');
  const [tags, setTags] = useState('');
  const [readingTime, setReadingTime] = useState('');
  const [publishDate, setPublishDate] = useState(null);
  const [instructorEmail, setInstructorEmail] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [status, setStatus] = useState('published');
  const [isPublic, setIsPublic] = useState(true); // 公開設定 (true=公開/OK, false=非公開/NG)（新規）
  const [attachedImages, setAttachedImages] = useState([]);

  // 本文プレビュートグル
  const [previewOn, setPreviewOn] = useState(false);

  // 画像アップロード進捗
  const [uploadingImage, setUploadingImage] = useState(false);

  // 本文 textarea への ref（一覧の「本文に挿入」 で cursor 位置に書き込むため）
  // Ant Design TextArea の ref は { resizableTextArea: { textArea: HTMLTextAreaElement }, ... }
  const contentMdRef = useRef(null);

  const [serverError, setServerError] = useState(null);

  // ─────────────────────────────────
  // データ取得
  // ─────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resArt, resSeries] = await Promise.all([
        // 編集画面は role 9 専用（Auth HOC でゲート済み）。非公開(NG)記事も取得できるよう role=9 を付与
        Axios.get(`/api/article/${params.articleId}?role=9`),
        Axios.get(`/api/articleSeries/${params.seriesCode}`),
      ]);
      if (resArt.data && resArt.data.success) {
        const a = resArt.data.article;
        setArticle(a);
        setTitle(a.title || '');
        setSubTitle(a.sub_title || '');
        setSummary(a.summary || '');
        setContentMd(a.content_md || '');
        setTarget((a.target || []).join(', '));
        setTags((a.tags || []).join(', '));
        setReadingTime(a.reading_time || '');
        setPublishDate(a.publish_date ? dayjs(a.publish_date) : null);
        setInstructorEmail(
          (a.instructor_id && typeof a.instructor_id === 'object'
            ? a.instructor_id.email
            : null) ||
            a.instructor_email ||
            ''
        );
        setThumbnail(a.thumbnail || '');
        setStatus(a.status || 'published');
        // is_public は未定義なら既定 true（公開）
        setIsPublic(a.is_public !== undefined ? a.is_public !== false : true);
        setAttachedImages(a.attached_images || []);
      } else {
        message.error('記事の取得に失敗しました');
      }
      if (resSeries.data && resSeries.data.success) {
        setSeries(resSeries.data.series);
      }
    } catch (err) {
      console.log(err);
      message.error('記事の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [params.articleId, params.seriesCode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─────────────────────────────────
  // 本文中の画像参照URL一覧を抽出（一覧の「削除」可否判定に利用）
  // - 一致基準は s3_url 完全一致
  // - 外部URL や別のキー文字列は対象外
  // ─────────────────────────────────
  const referencedUrls = useMemo(() => {
    const urls = new Set();
    const re = /!\[[^\]]*\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
    let m;
    while ((m = re.exec(contentMd || '')) !== null) {
      urls.add(m[1]);
    }
    return urls;
  }, [contentMd]);

  // ─────────────────────────────────
  // 本文 textarea へ markdown 挿入（cursor 位置に。ref が無ければ末尾追加）
  // ─────────────────────────────────
  const insertIntoContent = useCallback(
    (markdown) => {
      const ta =
        contentMdRef.current &&
        contentMdRef.current.resizableTextArea &&
        contentMdRef.current.resizableTextArea.textArea;
      if (!ta) {
        // ref が取れない（プレビュー切替直後など） → 末尾追加
        setContentMd((prev) => (prev || '') + '\n' + markdown + '\n');
        return;
      }
      const start = ta.selectionStart || 0;
      const end = ta.selectionEnd || 0;
      const before = (contentMd || '').slice(0, start);
      const after = (contentMd || '').slice(end);
      const next = before + markdown + after;
      setContentMd(next);
      // cursor を挿入直後に移動（state 反映後に実行するため次の tick）
      setTimeout(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = start + markdown.length;
      }, 0);
    },
    [contentMd]
  );

  // 一覧から markdown を生成
  const buildMarkdown = (a) => `![${a.original_name}](${a.s3_url})`;

  // 「Markdown コピー」: クリップボードへコピー
  const copyMarkdown = (a) => {
    const md = buildMarkdown(a);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(md)
        .then(() =>
          message.success('Markdown をクリップボードにコピーしました')
        )
        .catch(() => message.error('クリップボードへのコピーに失敗しました'));
    } else {
      message.warning('このブラウザはクリップボード操作に対応していません');
    }
  };

  // 「本文に挿入」: cursor 位置に markdown 書き込み
  const insertImage = (a) => {
    insertIntoContent(buildMarkdown(a));
    message.success('本文に挿入しました');
  };

  // ─────────────────────────────────
  // 新規画像のアップロード（dropzone から呼ばれる）
  // - サーバー: POST /api/article/:id/image
  // - 成功時: attachedImages に即時 push（DB も即時反映済み）
  // - 「保存」 を押さなくても画像 1 枚は永続化される
  // ─────────────────────────────────
  const onImageDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles && rejectedFiles.length > 0) {
      const r = rejectedFiles[0];
      const errMsg =
        (r.errors && r.errors[0] && r.errors[0].message) ||
        '無効なファイルです';
      message.error(`受け付けられません: ${errMsg}`);
      return;
    }
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    const f = acceptedFiles[0];
    const formData = new FormData();
    formData.append('image', f);

    setUploadingImage(true);
    Axios.post(`/api/article/${params.articleId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then((res) => {
        if (res.data && res.data.success) {
          setAttachedImages((prev) => [...prev, res.data.image]);
          message.success(
            `${res.data.image.original_name} をアップロードしました`
          );
        } else {
          message.error(res.data.error || 'アップロードに失敗しました');
        }
      })
      .catch((err) => {
        const msg =
          (err.response && err.response.data && err.response.data.error) ||
          'アップロードに失敗しました';
        message.error(msg);
      })
      .finally(() => setUploadingImage(false));
  };

  // ─────────────────────────────────
  // 添付画像の削除（一覧から外すのみ。S3 削除は保存時にバックエンドで実施）
  // 本文で参照中の画像はクライアント側でも削除を拒否（バックエンドでも二重チェック）
  // ─────────────────────────────────
  const removeImage = (s3_key) => {
    const target = attachedImages.find((a) => a.s3_key === s3_key);
    if (target && referencedUrls.has(target.s3_url)) {
      message.warning(
        '本文で参照中の画像は削除できません。先に本文の markdown 参照を取り除いてください'
      );
      return;
    }
    setAttachedImages((prev) => prev.filter((a) => a.s3_key !== s3_key));
    // サムネイルが削除された画像なら解除
    if (target && thumbnail === target.s3_url) {
      setThumbnail('');
    }
  };

  // ─────────────────────────────────
  // 保存
  // ─────────────────────────────────
  const onSave = async () => {
    setServerError(null);

    // クライアント側ミニマム検証
    if (!title.trim()) {
      message.error('タイトルは必須です');
      return;
    }
    if (!instructorEmail.trim()) {
      message.error('instructor_email は必須です');
      return;
    }
    if (!readingTime.trim()) {
      message.error('読了時間は必須です');
      return;
    }
    if (!publishDate) {
      message.error('公開日は必須です');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        sub_title: subTitle,
        is_public: isPublic, // boolean（バックエンドは boolean / 'OK'/'NG' を受理）
        summary,
        content_md: contentMd,
        target, // 백엔드가 CSV 분할
        tags,
        reading_time: readingTime.trim(),
        publish_date: publishDate.toISOString(),
        instructor_email: instructorEmail.trim(),
        thumbnail: thumbnail || null,
        status,
        attached_images: attachedImages,
      };
      const res = await Axios.patch(
        `/api/article/${params.articleId}`,
        payload
      );
      if (res.data && res.data.success) {
        message.success('記事を保存しました');
        // 編集後は本文ページへ戻る
        history.push(
          `/article/${params.seriesCode}/${params.articleId}`
        );
      } else {
        setServerError(res.data || { error: '保存に失敗しました' });
      }
    } catch (err) {
      const data = err.response && err.response.data;
      setServerError(data || { error: err.message || '保存に失敗しました' });
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────
  // 削除
  // ─────────────────────────────────
  const onDelete = async () => {
    setDeleting(true);
    try {
      const res = await Axios.delete(`/api/article/${params.articleId}`);
      if (res.data && res.data.success) {
        message.success(
          `記事を削除しました（添付画像 ${
            res.data.cleaned_image_count || 0
          } 枚も S3 から削除）`
        );
        history.push(`/article/${params.seriesCode}`);
      } else {
        message.error('削除に失敗しました');
      }
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        '削除に失敗しました';
      message.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // ─────────────────────────────────
  // レンダリング
  // ─────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!article) {
    return (
      <div style={{ padding: 40 }}>
        <EmptyState message="記事が見つかりません" />
      </div>
    );
  }

  // 所属表示用
  const ed = series
    ? (series.editions || []).find(
        (e) => e.edition_code === article.edition_code
      )
    : null;
  const cat = series
    ? (series.categories || []).find(
        (c) => c.category_code === article.category_code
      )
    : null;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <nav style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
        <a href="/">Home</a> ＞ <a href="/articleseries">記事一覧</a> ＞{' '}
        {series ? (
          <a href={`/article/${series.series_code}`}>{series.series_name}</a>
        ) : (
          'シリーズ'
        )}{' '}
        ＞{' '}
        <a href={`/article/${params.seriesCode}/${params.articleId}`}>
          {article.title}
        </a>{' '}
        ＞ <span style={{ color: '#333' }}>編集</span>
      </nav>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          記事編集
        </Title>
        <Space>
          <Button
            icon={<CloseCircleOutlined />}
            onClick={() =>
              history.push(
                `/article/${params.seriesCode}/${params.articleId}`
              )
            }
          >
            キャンセル
          </Button>
          <Popconfirm
            title="この記事を削除しますか？（添付画像も S3 から削除されます）"
            okText="削除"
            okType="danger"
            cancelText="キャンセル"
            onConfirm={onDelete}
          >
            <Button danger icon={<DeleteOutlined />} loading={deleting}>
              削除
            </Button>
          </Popconfirm>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onSave}
            loading={saving}
          >
            保存
          </Button>
        </Space>
      </div>

      {/* 所属情報（編集不可） */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <span>
            <Text strong>所属:</Text>{' '}
            シリーズ「{series ? series.series_name : '?'}」 / 編「
            {ed ? ed.edition_name : article.edition_name || '-'}」 / カテゴリ「
            {cat
              ? cat.category_name
              : article.category_name || '（カテゴリなし）'}
            」
          </span>
        }
        description="シリーズ・編・カテゴリの変更は本画面では行えません。シリーズホームの「編集モード」からドラッグ操作で移動してください。"
      />

      <Form layout="vertical">
        <Form.Item label="タイトル" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="記事タイトル"
          />
        </Form.Item>

        <Form.Item label="サブタイトル">
          <Input
            value={subTitle}
            onChange={(e) => setSubTitle(e.target.value)}
            placeholder="記事のサブタイトル（任意）"
          />
        </Form.Item>

        <Space size="large" style={{ display: 'flex' }}>
          <Form.Item
            label="著者 (instructor_email)"
            required
            style={{ flex: 1 }}
          >
            <Input
              value={instructorEmail}
              onChange={(e) => setInstructorEmail(e.target.value)}
              placeholder="xxx@yyy.zz"
            />
          </Form.Item>
          <Form.Item label="公開日" required>
            <DatePicker
              value={publishDate}
              onChange={setPublishDate}
              format="YYYY-MM-DD"
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item label="読了時間" required>
            <Input
              value={readingTime}
              onChange={(e) => setReadingTime(e.target.value)}
              placeholder="約12分"
              style={{ width: 140 }}
            />
          </Form.Item>
        </Space>

        <Space size="large" style={{ display: 'flex' }}>
          <Form.Item label="対象 (カンマ区切り)" style={{ flex: 1 }}>
            <Input
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="人事担当者, 管理職"
            />
          </Form.Item>
          <Form.Item label="タグ (カンマ区切り)" style={{ flex: 1 }}>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="人事の基礎, 人事ポリシー"
            />
          </Form.Item>
        </Space>

        <Form.Item label="サマリー（カード説明文）">
          <TextArea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            placeholder="省略時は本文先頭から自動抽出"
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <span>本文 (Markdown)</span>
              <Switch
                size="small"
                checked={previewOn}
                onChange={setPreviewOn}
                checkedChildren={<EyeOutlined />}
                unCheckedChildren={<EyeOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                プレビュー
              </Text>
            </Space>
          }
        >
          {previewOn ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              <TextArea
                ref={contentMdRef}
                value={contentMd}
                onChange={(e) => setContentMd(e.target.value)}
                rows={20}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e8e8e8',
                  borderRadius: 4,
                  padding: 16,
                  maxHeight: 480,
                  overflowY: 'auto',
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {contentMd}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <TextArea
              ref={contentMdRef}
              value={contentMd}
              onChange={(e) => setContentMd(e.target.value)}
              rows={20}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          )}
        </Form.Item>

        <Form.Item label="サムネイル">
          <Select
            value={thumbnail || ''}
            onChange={setThumbnail}
            allowClear
            style={{ width: '100%' }}
            placeholder="添付画像から選択（無しでも可）"
          >
            <Option value="">（なし）</Option>
            {attachedImages.map((a) => (
              <Option key={a.s3_key} value={a.s3_url}>
                {a.original_name}
              </Option>
            ))}
          </Select>
          {thumbnail && (
            <div style={{ marginTop: 8 }}>
              <img
                src={thumbnail}
                alt="thumbnail preview"
                style={{ maxHeight: 100, borderRadius: 4 }}
              />
            </div>
          )}
        </Form.Item>

        <Form.Item label="添付画像">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
            message="本文の画像参照と添付画像は連動します。本文で参照中の画像は一覧から削除できません（先に本文の markdown 参照を取り除いてください）。"
          />

          {/* ─── 新規画像ドロップゾーン（即時アップロード + DB 反映） ─── */}
          <Dropzone
            onDrop={onImageDrop}
            multiple={false}
            maxSize={30 * 1024 * 1024}
            accept="image/png,image/jpeg,image/gif,image/webp"
            disabled={uploadingImage}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                {...getRootProps()}
                style={{
                  marginBottom: 12,
                  padding: 16,
                  border: `2px dashed ${
                    isDragActive ? 'var(--blue-default)' : '#d9d9d9'
                  }`,
                  borderRadius: 6,
                  textAlign: 'center',
                  background: isDragActive ? '#e6f7ff' : '#fafafa',
                  cursor: uploadingImage ? 'wait' : 'pointer',
                }}
              >
                <input {...getInputProps()} />
                {uploadingImage ? (
                  <Space>
                    <Spin />
                    <Text>アップロード中…</Text>
                  </Space>
                ) : (
                  <>
                    <InboxOutlined
                      style={{
                        fontSize: 24,
                        color: 'var(--blue-default)',
                        marginRight: 8,
                      }}
                    />
                    <Text>
                      {isDragActive
                        ? 'ここにドロップ'
                        : '画像をドラッグ&ドロップ、またはクリックして選択（PNG/JPG/GIF/WebP, 30MB 以内）'}
                    </Text>
                  </>
                )}
              </div>
            )}
          </Dropzone>

          {attachedImages.length === 0 ? (
            <Text type="secondary">（添付画像なし）</Text>
          ) : (
            <div>
              {attachedImages.map((a) => {
                const isReferenced = referencedUrls.has(a.s3_url);
                return (
                  <div
                    key={a.s3_key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '6px 0',
                      borderBottom: '1px dashed #eee',
                    }}
                  >
                    <img
                      src={a.s3_url}
                      alt={a.original_name}
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 3,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13 }}>
                        {a.original_name}
                        {isReferenced && (
                          <StatusBadge
                            tone="warning"
                            style={{ marginLeft: 8, fontSize: 11 }}
                          >
                            本文で参照中
                          </StatusBadge>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#999' }}>
                        {a.s3_key}
                      </div>
                    </div>
                    <Space size="small">
                      <Tooltip title="Markdown 形式 ![alt](URL) をクリップボードにコピー">
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyMarkdown(a)}
                        >
                          コピー
                        </Button>
                      </Tooltip>
                      <Tooltip title="本文の cursor 位置に Markdown を挿入">
                        <Button
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => insertImage(a)}
                        >
                          本文に挿入
                        </Button>
                      </Tooltip>
                      {isReferenced ? (
                        <Tooltip title="本文で参照中。先に本文の markdown 参照を取り除いてください">
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            disabled
                          />
                        </Tooltip>
                      ) : (
                        <Popconfirm
                          title="この画像を削除しますか？保存時に S3 からも削除されます"
                          okText="削除"
                          okType="danger"
                          cancelText="キャンセル"
                          onConfirm={() => removeImage(a.s3_key)}
                        >
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                );
              })}
            </div>
          )}
        </Form.Item>

        <Form.Item label="公開状態">
          <Radio.Group
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <Radio value="published">公開</Radio>
            <Radio value="draft">下書き</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="公開設定"
          help="非公開(NG)の記事は role 1・7 には表示されません。"
        >
          <Radio.Group
            value={isPublic}
            onChange={(e) => setIsPublic(e.target.value)}
          >
            <Radio value={true}>公開（OK）</Radio>
            <Radio value={false}>非公開（NG）</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>

      {/* サーバーエラー表示 */}
      {serverError && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message={serverError.error || '保存に失敗しました'}
          description={
            <div>
              {serverError.field && (
                <div>
                  <Text type="secondary">問題のあるフィールド:</Text>{' '}
                  <StatusBadge tone="error">{serverError.field}</StatusBadge>
                </div>
              )}
              {serverError.input_value !== undefined && (
                <div>
                  <Text type="secondary">入力値:</Text>{' '}
                  <Text code>{String(serverError.input_value)}</Text>
                </div>
              )}
              {serverError.existing_article_id && (
                <div>
                  <Text type="secondary">既存記事 _id:</Text>{' '}
                  <Text code>{serverError.existing_article_id}</Text>
                </div>
              )}
              {Array.isArray(serverError.blocking_image_refs) &&
                serverError.blocking_image_refs.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <Text type="secondary">本文で参照中の画像:</Text>
                    <ul style={{ paddingLeft: 18, marginTop: 4 }}>
                      {serverError.blocking_image_refs.map((b) => (
                        <li key={b.s3_url} style={{ fontSize: 12 }}>
                          {b.original_name}（
                          <Text code style={{ fontSize: 11 }}>
                            {b.s3_url}
                          </Text>
                          ）
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              {serverError.hint && (
                <div style={{ marginTop: 6 }}>
                  <Text type="secondary">ヒント:</Text> {serverError.hint}
                </div>
              )}
            </div>
          }
        />
      )}

      <Divider />
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        所属（シリーズ・編・カテゴリ・表示順）の変更は本画面では行えません。
        シリーズホームの「編集モード」からドラッグ操作で移動してください。
      </Paragraph>
    </div>
  );
}

export default ArticleEditPage;
